import { z } from "zod";

/**
 * Actions of the permission function `can(actor, action, subject)` in `packages/core`
 * (07 §2.4). The session carries the actor's allowed actions as this list, so the portal
 * shows and hides areas by the same names the API enforces (K-04, 05 §6.2); buttons on a
 * record follow its `actions` field (ADR-075).
 */
export const Permission = z.enum([
  // every role, own customer; staff every customer
  "catalog.view",
  "customer.view",
  "entitlement.view",
  // administrator of the customer; staff every customer (03 §1, ADR-074)
  "usage.view",
  "history.view",
  "order.view",
  "user.view",
  // administrator of the customer
  "order.place",
  "order.cancel",
  "demo.request",
  // the cancellation of a living entitlement; in the alpha release through an inquiry, the action
  // only says whether the portal shows how to cancel (UC-16, Weg B of the TL, 2026-10-14)
  "entitlement.cancel",
  // user and administrator of the customer, not staff: an inquiry from the portal (ADR-093)
  "inquiry.create",
  // administrator, support, admin
  "limit.set",
  // support, admin
  "customer.approve",
  "credit_limit.set",
  "order.process",
  "task.complete",
  "key.reveal",
  "report.freeze",
  "correction.book",
  "invoice.book",
  "inquiry.process",
  // support, admin: the products with their terms and the history of the terms, read only
  // (UC-26, ADR-125 addendum 2026-10-19)
  "product.view",
  // admin only: the terms of a product (UC-26, AP-B8.4)
  "product.configure",
  // admin only: deleting an unpublished product nothing uses, its histories stay (ADR-148)
  "product.delete",
  // support, admin (AP-B8.5): suspending an approved customer and reinstating a suspended one
  // (UC-18), the routes decide the direction
  "customer.suspend",
  "instance.change",
  "role.set",
  "key.rotate",
  "entitlement.convert",
  "entitlement.revoke",
  // support, admin (UC-16, ADR-137): the termination of a living entitlement with a reason,
  // effective at once; the customer's `entitlement.cancel` only shows how to ask for it
  "entitlement.terminate",
  // support, admin (ADR-133): creating a customer (UC-20), decided by the role alone, and placing
  // an order on a customer's behalf (UC-32), refused for a suspended customer
  "customer.create",
  "order.create",
  // admin, v1
  "staff.manage",
]);
export type Permission = z.infer<typeof Permission>;

/**
 * Why an action is not allowed (07 §2.4 Bedingung, K-04 `reason`). The API puts the code on
 * `403` and `409` responses, the portal maps it to one text, so the user reads the same
 * explanation before and after clicking. The list grows with the use cases; a new code is a
 * contract change.
 */
export const ReasonCode = z.enum([
  /** the role may not perform the action at all */
  "role_required",
  /**
   * the customer's own ordering, demo and cancellation are switched off (`SELF_SERVICE_ENABLED`,
   * ADR-133): in the beta the team orders on the customer's behalf (UC-32); checked right after
   * the role, before every other reason
   */
  "self_service_off",
  /** customer is suspended: no orders, demos or cancellations (07 §2.4) */
  "customer_suspended",
  /** customer not approved yet: no limits, no provisioning (R-02, R-03) */
  "customer_not_approved",
  /** the sum of monthly limits would exceed the credit limit (R-04, UC-10) */
  "credit_limit_exceeded",
  /** the entitlement is not pending, active or paused (07 §2.4 "Berechtigung lebend") */
  "entitlement_not_live",
  /** the limit of a demo comes from the product and cannot be changed (UC-06, UC-10) */
  "demo_limit_fixed",
  /** a living entitlement for this product exists already (R-01, UC-03) */
  "entitlement_exists",
  /** an open order for this product exists already (ADR-029) */
  "order_open",
  /** the customer had a demo of this product before; a second one only through staff (R-08) */
  "demo_already_used",
  /** the product offers no demo */
  "demo_not_offered",
  /** the product has billing model demo: it comes through "Demo starten", not through ordering (ADR-082 point 4) */
  "demo_only",
  /** the product is only on request: no order and no demo, the inquiry form instead (ADR-112) */
  "inquiry_only",
  /**
   * the period is invoiced: no second invoice, no freeze; a correction is not refused but booked
   * into the first open month (07 §3.6, UC-13, ADR-138 point 3)
   */
  "period_invoiced",
  /** a priced order cannot be completed without an invoice reference (UC-04) */
  "invoice_reference_required",
  /** an order cannot be completed before the provisioning of its entitlement is done (UC-04 step 5) */
  "provisioning_open",
  /** the order is no longer open (completed or cancelled) */
  "order_closed",
  /** staff work on the order already; only staff can cancel it now (UC-23, TL 2026-10-14) */
  "order_in_progress",
  /** the order carries an invoice reference; only staff can cancel it now (UC-23, TL 2026-10-14) */
  "order_invoiced",
  /** the customer is approved already (UC-05) */
  "customer_already_approved",
  /** the customer is suspended already; suspending again changes nothing (UC-18, AP-B8.5) */
  "customer_already_suspended",
  /** the customer is not suspended; nothing to reinstate (UC-18, AP-B8.5) */
  "customer_not_suspended",
  /** the instance already belongs to another customer (R-17, UC-22, AP-B8.5) */
  "instance_in_use",
  /** the task is done or cancelled (UC-08, UC-24) */
  "task_done",
  /** the entitlement has no active key to reveal (UC-08) */
  "key_missing",
  /**
   * the entitlement runs without the platform's AI keys (provider other than `ai_cockpit`,
   * ADR-133): no key and no monthly limit to set
   */
  "no_ai_access",
  /** the usage report is frozen already (UC-13, UC-14) */
  "report_frozen",
  /** the inquiry is closed; it moves forward only (UC-30) */
  "inquiry_closed",
  /**
   * an order, an entitlement or an inquiry refers to the product: it stays (ADR-148 point 2);
   * named before `product_published`, since it is final
   */
  "product_in_use",
  /**
   * dataCycle still publishes the product: the next sync would create it again; taken out of the
   * publication in dataCycle first (ADR-148 point 2)
   */
  "product_published",
]);
export type ReasonCode = z.infer<typeof ReasonCode>;

/**
 * The decision of `can` for one action on one record (ADR-075, 05 §6.2): allowed, or not
 * allowed with the reason the portal shows. The same code comes back on `403` and `409` when
 * the action is attempted anyway (K-04).
 */
export const ActionDecision = z.discriminatedUnion("allowed", [
  z.strictObject({ allowed: z.literal(true) }),
  z.strictObject({ allowed: z.literal(false), reason: ReasonCode }),
]);
export type ActionDecision = z.infer<typeof ActionDecision>;

/**
 * The `actions` field of a record: one decision per action that exists on that record, with
 * fixed keys so the contract says which buttons a record has (ADR-075). Only what is decided
 * by role and state belongs here; what depends on the input (`credit_limit_exceeded`,
 * `invoice_reference_required`) stays a rejection after submitting.
 *
 *   actions: actionsOf("order.place", "demo.request")
 */
export function actionsOf<const K extends readonly [Permission, ...Permission[]]>(...keys: K) {
  const shape = Object.fromEntries(keys.map((key) => [key, ActionDecision])) as {
    [P in K[number]]: typeof ActionDecision;
  };
  return z.strictObject(shape);
}
